import { reportAudioUnavailable } from '../../../utils/safeAudioPlay';
import { playableRecordingUrl } from '../../../utils/appleAudio';

/**
 * Audio playback over in-memory blob URLs. Resolves two competing concerns:
 *
 * 1. WebKit deallocates decoder state on idle <audio> elements pointing at
 *    REMOTE URLs, corrupting `duration` on next play. Caching the bytes as a
 *    blob URL keeps playback local — no network stream to reclaim.
 *
 * 2. AudioBufferSourceNode.playbackRate is a sample-rate change (chipmunk
 *    voices at >1x). HTMLAudioElement.playbackRate has `preservesPitch=true`
 *    by default — variable speed without pitch shift. Required for the
 *    teacher review + lesson play UIs which both expose speed controls.
 *
 * The exported names are kept ("loadAudioBuffer", "playAudioBuffer", …) so
 * call-sites don't change. The cache stores blob URLs, not AudioBuffers.
 */

const blobUrlCache = new Map();
const inFlight = new Map();

export function loadAudioBuffer(url) {
  // Blob URLs (e.g. MediaRecorder output) are already in-memory; fetch+re-blob
  // would just double-allocate. Pass through. This also avoids a leak when a
  // re-record replaces the URL — we never owned the lifecycle, original code does.
  if (url.startsWith('blob:')) return Promise.resolve(url);
  if (blobUrlCache.has(url)) return Promise.resolve(blobUrlCache.get(url));
  if (inFlight.has(url)) return inFlight.get(url);

  const promise = (async () => {
    // On iPhones/iPads a recording comes as its AAC copy (utils/appleAudio); the cache keys on the original.
    const res = await fetch(playableRecordingUrl(url));
    if (!res.ok) throw new Error(`fetch ${res.status}`);
    const arrayBuffer = await res.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: res.headers.get('Content-Type') || 'audio/mpeg' });
    const blobUrl = URL.createObjectURL(blob);
    blobUrlCache.set(url, blobUrl);
    inFlight.delete(url);
    return blobUrl;
  })();

  inFlight.set(url, promise);
  promise.catch(() => inFlight.delete(url));
  return promise;
}

export function preloadAudioBuffers(urls) {
  for (const url of urls) {
    // Per-URL failures are surfaced at play-time via reportAudioUnavailable.
    loadAudioBuffer(url).catch(() => {});
  }
}

/**
 * Revoke all cached blob URLs and clear the cache.
 *
 * Cache is module-global. Do NOT call from a component's unmount — would
 * revoke blob URLs other mounted components still hold (their `audio.src`
 * would 404 against a revoked blob). Reserved for app-level teardown only.
 */
export function clearBlobCache() {
  for (const blobUrl of blobUrlCache.values()) {
    try { URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
  }
  blobUrlCache.clear();
  inFlight.clear();
}

export function playAudioBuffer(url, options = {}) {
  const { threshold = 0.9, onThreshold, onEnded, onError, playbackRate = 1 } = options;
  let cancelled = false;
  let audio = null;
  let thresholdFired = false;

  const cleanup = () => {
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.ontimeupdate = null;
    }
  };

  loadAudioBuffer(url).then((blobUrl) => {
    if (cancelled) return;
    audio = new Audio(blobUrl);
    audio.preservesPitch = true;
    // iOS Safari 14.x and some desktop Safari builds silently ignore the
    // unprefixed property and need the webkit alias.
    audio.webkitPreservesPitch = true;
    audio.playbackRate = playbackRate;

    audio.ontimeupdate = () => {
      if (cancelled || thresholdFired) return;
      const d = audio.duration;
      if (d > 0 && isFinite(d) && audio.currentTime / d >= threshold) {
        thresholdFired = true;
        onThreshold?.();
      }
    };

    audio.onended = () => {
      if (cancelled) return;
      cleanup();
      onEnded?.();
    };

    audio.onerror = () => {
      if (cancelled) return;
      cleanup();
      reportAudioUnavailable(url, `audio.error: ${audio.error?.message ?? `code=${audio.error?.code}`}`);
      onError?.(audio.error);
    };

    audio.play().catch((err) => {
      if (cancelled) return;
      // NotAllowedError = autoplay blocked / missing user gesture. Not a media
      // failure — don't pollute the broken-audio Sentry channel with it.
      if (err?.name === 'NotAllowedError') {
        cleanup();
        onError?.(err);
        return;
      }
      cleanup();
      reportAudioUnavailable(url, `play: ${err?.message ?? String(err)}`);
      onError?.(err);
    });
  }).catch((err) => {
    if (cancelled) return;
    cleanup();
    reportAudioUnavailable(url, `load: ${err?.message ?? String(err)}`);
    onError?.(err);
  });

  return {
    stop() {
      cancelled = true;
      cleanup();
      if (audio) {
        try { audio.pause(); } catch { /* already stopped */ }
        audio.removeAttribute('src');
        audio.load();
      }
    },
    setPlaybackRate(rate) {
      if (audio) audio.playbackRate = rate;
    },
  };
}
